"use client";
import { FormField } from "@/ui/form";
import { cn } from "@gettactic/helpers/src/css/cn";
import { useAuthenticated } from "@gettactic/hooks";
import { useEffect, useState, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  getAvailableWeekDays,
  isDayDisabled,
  weekDays,
  weekendDays
} from "./anchor-days-utils";
import { WeekDayNames } from "@gettactic/api";

export type AnchorDaysObject = Record<WeekDayNames, boolean>;

type AnchorDayPickerProps = {
  form: UseFormReturn<any>;
  anchorDays: AnchorDaysObject;
  currentDayIdx: number;
  singleSelect?: boolean;
  name?: string;
  minDate?: Date;
  maxDate?: Date;
  label?: string;
};
const defaultAnchorDays: AnchorDaysObject = {
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false
};

export function AnchorDayPicker({
  form,
  anchorDays,
  currentDayIdx,
  singleSelect = false,
  name = "anchorDays",
  minDate,
  label,
  maxDate
}: AnchorDayPickerProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const hideWeekends =
    authenticatedUser.organization?.features?.includes("hide.weekends") ??
    false;

  const [localAnchorDays, setLocalAnchorDays] = useState<AnchorDaysObject>(
    anchorDays || defaultAnchorDays
  );

  useEffect(() => {
    setLocalAnchorDays(anchorDays || defaultAnchorDays);
  }, [anchorDays]);

  const handleDaySelection = (day: WeekDayNames) => {
    const newAnchorDays = { ...localAnchorDays };

    if (singleSelect) {
      Object.keys(newAnchorDays).forEach((key) => {
        newAnchorDays[key as WeekDayNames] = false;
      });
      newAnchorDays[day] = true;
    } else {
      newAnchorDays[day] = !newAnchorDays[day];
    }

    form.setValue(name, newAnchorDays);
  };

  const availableWeekdays = useMemo(() => {
    return getAvailableWeekDays({
      minDate,
      maxDate,
      defaultAnchorDays
    });
  }, [minDate, maxDate]);

  useEffect(() => {
    if (!anchorDays) return;

    let needsUpdate = false;
    const updatedAnchorDays = { ...anchorDays };

    // unselect disabled days
    Object.keys(updatedAnchorDays).forEach((day) => {
      const typedDay = day as WeekDayNames;
      if (
        updatedAnchorDays[typedDay] &&
        isDayDisabled(
          currentDayIdx,
          typedDay,
          minDate,
          maxDate,
          availableWeekdays
        )
      ) {
        updatedAnchorDays[typedDay] = false;
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      form.setValue(name, updatedAnchorDays);
    }
  }, [minDate, maxDate, availableWeekdays, anchorDays]);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <div className="mt-2 flex items-center">
          <div
            className={`${hideWeekends ? (label ? "basis-2/4" : "basis-3/4") : "basis-4/4"} flex items-center justify-between space-x-1`}
          >
            {weekDays
              .filter((day) =>
                hideWeekends ? !weekendDays.includes(day.value) : true
              )
              .map((day) => {
                const isSelected = localAnchorDays[day.value] === true;
                const isDisabled = isDayDisabled(
                  currentDayIdx,
                  day.value,
                  minDate,
                  maxDate,
                  availableWeekdays
                );

                return (
                  <div
                    key={day.value}
                    onClick={() =>
                      !isDisabled ? handleDaySelection(day.value) : null
                    }
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-full text-center font-bold capitalize ",
                      isSelected
                        ? "bg-primary text-white "
                        : "border border-2 border-neutral-300 text-secondary-foreground",
                      isDisabled
                        ? "cursor-not-allowed opacity-30"
                        : "cursor-pointer"
                    )}
                    title={day.label}
                  >
                    {day.label[0]}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    />
  );
}
